import React, { useCallback, useMemo } from 'react';
import { eWeekdays, IAdminWorkSchedule } from '../../../types/userProfile/IUserProfile';
import { checkForMutationSuccess } from '../../../API/checkForMutationSuccess';
import { useUpdateAdminUserProfileMutation } from '../../../API/administration/AdminUsersAPI';
import { Form, Formik } from 'formik';
import { IAdminUserData } from '../../../types/administration/userAdministration/IAdminUser';
import { editProfilePageForm } from '../../../validation/userProfile/ProfilePageForm';
import { TextFieldTW } from '../../../tailwindComponents/textInputTW/TextFieldTW';
import { LocationTimeZoneSelect } from '../../userProfile/formElements/LocationTimeZoneSelect';
import { PhoneNumberFieldTW } from '../../../tailwindComponents/phoneNumberFieldTW/PhoneNumberFieldTW';
import { NumberField } from '../../../ui-toolkit';
import { LanguagesSelect } from '../../userProfile/formElements/LanguagesSelect';
import { OrderContactNotesField } from '../../order/contacts/subComponents/OrderContactNotesField';
import { WorkScheduleFields } from '../../userProfile/formElements/WorkScheduleFields';
import { ErrorMessageTW } from '../../../tailwindComponents/errorMessage/ErrorMessageTW';
import { SubmissionFooter } from '../../userProfile/formElements/SubmissionFooter';
import { useHasPermissions } from '../../accessControl/hooks/useHasPermissions';
import { ePermissions } from '../../../types/administration/userAdministration/ePermissions';
import { SlideOver } from '../../../tailwindComponents/slideOver/SlideOver';
import { AdminTitle } from '../../shared/AdminTitle';

interface EditUserProfileFormState {
  phoneNumber: string | null;
  phoneExtension: number | null;
  externalEmail: string | null;
  timeZone: string | null;
  notes: string | null;
  pronouns: string | null;
  workSchedule: Partial<Record<eWeekdays, IAdminWorkSchedule>>;
  languages: string[];
}

const cleanObject = (formikState: EditUserProfileFormState): EditUserProfileFormState => {
  const workSchedule: Partial<Record<eWeekdays, IAdminWorkSchedule>> = {};
  Object.entries(formikState.workSchedule)
    .filter(([, { startTime, endTime }]) => {
      // Include entries that have at least one time field filled and are valid (no underscores)
      const hasValidStartTime = startTime && startTime.trim() !== '' && startTime.indexOf('_') === -1;
      const hasValidEndTime = endTime && endTime.trim() !== '' && endTime.indexOf('_') === -1;
      return hasValidStartTime || hasValidEndTime;
    })
    .forEach(([workday, adminWorkSchedule]) => {
      // Clean invalid time values (with underscores) before sending to backend
      workSchedule[workday as eWeekdays] = {
        startTime: adminWorkSchedule.startTime && adminWorkSchedule.startTime.indexOf('_') === -1
          ? adminWorkSchedule.startTime
          : '',
        endTime: adminWorkSchedule.endTime && adminWorkSchedule.endTime.indexOf('_') === -1
          ? adminWorkSchedule.endTime
          : '',
      };
    });
  return {
    phoneNumber: formikState.phoneNumber || null,
    phoneExtension: formikState.phoneExtension || null,
    externalEmail: formikState.externalEmail?.trim() || null,
    timeZone: formikState.timeZone || null,
    notes: formikState.notes || null,
    pronouns: formikState.pronouns || null,
    languages: formikState.languages,
    workSchedule,
  };
};

export const EditUserProfileForm: React.FC<{
  adminUser: IAdminUserData | null;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ adminUser, open, setOpen }) => {
  const [updateAdminUserProfile, { error, isLoading }] = useUpdateAdminUserProfileMutation();
  const canEditExternalEmail = useHasPermissions([ePermissions.EditExternalEmail]);

  const handleUpdateSubmit = useCallback(
    async (values: EditUserProfileFormState) => {
      if (!adminUser) return;
      
      const cleanedValues = cleanObject(values);
      const response = await updateAdminUserProfile({
        id: adminUser.id,
        ...cleanedValues,
      });
      if (checkForMutationSuccess(response, 'updating user profile')) {
        setOpen(false);
      }
    },
    [updateAdminUserProfile, setOpen, adminUser]
  );

  const initialValues: EditUserProfileFormState = useMemo(
    () => ({
      phoneNumber: adminUser?.phoneNumber ?? null,
      phoneExtension: adminUser?.phoneExtension ?? null,
      externalEmail: adminUser?.externalEmail ?? null,
      timeZone: adminUser?.timeZone ?? null,
      notes: adminUser?.notes ?? null,
      pronouns: adminUser?.pronouns ?? null,
      languages: adminUser?.languages ?? [],
      workSchedule: adminUser?.workSchedule ?? {},
    }),
    [adminUser]
  );

  const validationSchema = useMemo(
    () => editProfilePageForm(canEditExternalEmail),
    [canEditExternalEmail]
  );

  return (
    <SlideOver 
      open={open} 
      setOpen={setOpen} 
      title={<AdminTitle fullName={adminUser?.fullName ?? ''} email={adminUser?.email ?? ''} />}
    >
      <ErrorMessageTW rtkError={error} />
      <Formik
        initialValues={initialValues}
        onSubmit={handleUpdateSubmit}
        validationSchema={validationSchema}
        enableReinitialize
      >
        <Form>
          <div className={'flex flex-col gap-2'}>
            <TextFieldTW name={'pronouns'} labelText={'Pronouns'} />
            <LocationTimeZoneSelect />
            <PhoneNumberFieldTW name={'phoneNumber'} labelText={'Phone Number'} />
            <NumberField name={'phoneExtension'} label={'Phone Extension'} />
            {canEditExternalEmail && (
              <TextFieldTW name={'externalEmail'} labelText={'External Email'} />
            )}
            <WorkScheduleFields />
            <LanguagesSelect />
            <OrderContactNotesField name={'notes'} />
            <SubmissionFooter onCancel={() => setOpen(false)} disabled={isLoading} />
          </div>
        </Form>
      </Formik>
    </SlideOver>
  );
};


